import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import AppHeader from './components/common/AppHeader';
import LiveBetaBanner from './components/common/LiveBetaBanner';
import SiteFeedbackModal from './components/common/SiteFeedbackModal';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import authManager from './utils/authManager';
import Home from './pages/Home';
import LandingPage from './pages/LandingPage';
import LandingLayout from './components/landing/LandingLayout';
import LandingNav from './components/landing/LandingNav';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import MiddlesexLeaguePage from './pages/leagues/MiddlesexLeaguePage';
import LeagueAdminPage from './pages/LeagueAdminPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ClubSessionStartRedirect from './pages/ClubSessionStartRedirect';
import ClubSessionWrapUpRedirect from './pages/ClubSessionWrapUpRedirect';
import Session from './pages/Session';
import SessionDashboard from './pages/SessionDashboard';
import ClubAdmin from './pages/ClubAdmin';
import PaymentTrackingPage from './pages/PaymentTrackingPage';
import PlayerDashboard from './pages/PlayerDashboard';
import PlayerProfilePage from './pages/PlayerProfilePage';
import PlayerSessionPerformancePage from './pages/PlayerSessionPerformancePage';
import ProfileLayout from './components/profile/ProfileLayout';
import ProfileOverview from './pages/profile/ProfileOverview';
import ProfileClubsList from './pages/profile/ProfileClubsList';
import ProfileMyClubs from './pages/profile/ProfileMyClubs';
import ProfileClubManagement from './pages/profile/ProfileClubManagement';
import ProfileSettings from './pages/profile/ProfileSettings';
import ProfileClubPerformance from './pages/profile/ProfileClubPerformance';
import ProfileClubH2H from './pages/profile/ProfileClubH2H';
import ProfileClubPartnerships from './pages/profile/ProfileClubPartnerships';
import ProfileMyGames from './pages/profile/ProfileMyGames';
import ProfileMySessions from './pages/profile/ProfileMySessions';
import ProfileHeadToHead from './pages/profile/ProfileHeadToHead';
import CreateClub from './pages/CreateClub';
import ClubPage from './pages/ClubPage';
import FullLeaderboardPage from './pages/FullLeaderboardPage';
import UpcomingSessions from './pages/UpcomingSessions';
import SessionManagement from './pages/SessionManagement';
import SessionSignupsPage from './pages/SessionSignupsPage';
import SessionStartPage from './pages/SessionStartPage';
import SessionReviewPage from './pages/SessionReviewPage';
import ClubSessionPlanning from './pages/ClubSessionPlanning';
import SessionWrapUpDetail from './pages/SessionWrapUpDetail';
import ClubManagement from './pages/ClubManagement';
import ClubLeagueSquadPage from './pages/ClubLeagueSquadPage';
import PasteAliasManagementPage from './pages/PasteAliasManagementPage';
import ClubLeaguePlayerPerformancePage from './pages/ClubLeaguePlayerPerformancePage';
import ClubAnalysis from './pages/ClubAnalysis';
import SessionSimulation from './pages/SessionSimulation';
import ClubSettings from './pages/ClubSettings';
import Clubs from './pages/Clubs';
import AccountSettings from './pages/AccountSettings';
import ResetPassword from './pages/ResetPassword';
import ReleaseNotes from './pages/ReleaseNotes';
import MatchmakingInfo from './pages/MatchmakingInfo';
import PrivateSessionsPage from './pages/PrivateSessionsPage';
import { supabase } from './utils/supabase';
import { SeasonProvider } from './context/SeasonContext';
import { Modal, ModalContent } from './components/common';
import TournamentsPage from './pages/TournamentsPage';
import CreateTournamentPage from './pages/CreateTournamentPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import Breadcrumb from './components/common/Breadcrumb';
import { resolveSessionForApp, isAuthShellPath } from './utils/resolveAppAuth';
import { getUnreadNotificationCount } from './services/notificationService';
import NotificationsTab from './components/notifications/NotificationsTab';
import AuthCallback from './pages/AuthCallback';
import CompleteProfile from './pages/CompleteProfile';
import { loginRedirectWithFrom, safeReturnPath } from './utils/safeReturnPath';

// Redirect component for old profile club routes to Club Dashboard
const ClubRouteRedirect = ({ tab }) => {
  const { clubId } = useParams();
  return <Navigate to={`/clubs/${clubId}?tab=${tab}`} replace />;
};

const PublicCatchAll = () => {
  const location = useLocation();
  return <Navigate to={loginRedirectWithFrom(location.pathname, location.search)} replace />;
};

function App() {
  useEffect(() => {
    // console.log('[App] MOUNTED');
  }, []);
  useEffect(() => {
    // console.log('[App] RENDER');
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authResolving, setAuthResolving] = useState(false);
  const sessionRef = useRef(null);
  const [error, setError] = useState(null);
  // Add selectedClub state to App if not already present
  const [selectedClub, setSelectedClub] = useState(null);

  // Router location (used to special-case reset-password layout)
  const location = useLocation();
  const navigate = useNavigate();
  const isResetPasswordRoute = location.pathname === '/reset-password';
  const isPublicLegalRoute = location.pathname === '/privacy';
  // OAuth routes bypass the !currentUser gate (same pattern as reset-password).
  // On these paths we skip ensurePlayerRecord and currentUser hydration so that
  // AuthCallback / CompleteProfile can own session handling without races.
  const isAuthOnboardingRoute =
    location.pathname === '/auth/callback' ||
    location.pathname === '/auth/complete-profile';
  const locationRef = useRef(location);
  locationRef.current = location;

  const authResolveCallbacksRef = useRef(null);
  authResolveCallbacksRef.current = {
    setCurrentUser,
    navigate,
    setError,
    onSignOut: async () => {
      await supabase.auth.signOut();
    },
  };

  useEffect(() => {
    if (currentUser) {
      document.title = 'Shuttlr';
    }
  }, [currentUser]);

  // Notifications state
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSiteFeedbackModal, setShowSiteFeedbackModal] = useState(false);
  
  // Load unread notification count when user is logged in
  useEffect(() => {
    if (currentUser?.id) {
      const loadNotificationCount = async () => {
        try {
          const count = await getUnreadNotificationCount(currentUser.id);
          setUnreadNotificationCount(count);
        } catch (err) {
          console.error('Error loading notification count:', err);
        }
      };
      
      loadNotificationCount();
      // Refresh count every 30 seconds
      const interval = setInterval(loadNotificationCount, 30000);
      return () => clearInterval(interval);
    } else {
      setUnreadNotificationCount(0);
    }
  }, [currentUser?.id]);

  // Clear any stale sessions on mount
  useEffect(() => {
    const clearStaleSession = async () => {
      console.log('Checking for stale session...');
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (session) {
        console.log('Found existing session, checking if it\'s valid...');
        // Only clear the session if it's actually stale (expired)
        const now = Math.floor(Date.now() / 1000);
        if (session.expires_at && session.expires_at < now) {
          console.log('Session is expired, signing out to clear state...');
          await supabase.auth.signOut();
          window.location.reload(); // Reload to ensure clean state
          return;
        } else {
          console.log('Session is still valid, proceeding with normal initialization');
        }
      }
      
      console.log('No stale session found, proceeding with normal initialization');
      setLoading(false);
    };

    clearStaleSession();
  }, []);

  // Only initialize auth after checking for stale sessions
  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const runResolve = async (session, pathname) => {
      if (cancelled) return;
      sessionRef.current = session;
      const needsResolve = session?.user && !isAuthShellPath(pathname);
      if (needsResolve) setAuthResolving(true);
      try {
        await resolveSessionForApp(session, pathname, authResolveCallbacksRef.current);
      } catch (err) {
        if (!cancelled) {
          console.error('Auth resolution error:', err);
          setError(err.message);
          setCurrentUser(null);
        }
      } finally {
        if (!cancelled) {
          setAuthResolving(false);
          setLoading(false);
        }
      }
    };

    async function initializeAuth() {
      console.log('Starting fresh auth initialization...');
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        await runResolve(session, location.pathname);
      } catch (err) {
        if (!cancelled) {
          console.error('Auth initialization error:', err);
          setError(err.message);
          setCurrentUser(null);
          setAuthResolving(false);
          setLoading(false);
        }
      }
    }

    initializeAuth();

    const unsubscribe = authManager.addListener((_event, session) => {
      console.log('Auth state changed:', _event, session);
      setTimeout(() => {
        if (cancelled) return;

        if (!session?.user) {
          sessionRef.current = null;
          setAuthResolving(false);
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        const currentPath = locationRef.current.pathname;

        if (isAuthShellPath(currentPath)) {
          sessionRef.current = session;
          setLoading(false);
          return;
        }

        sessionRef.current = session;
        setAuthResolving(true);
        resolveSessionForApp(session, currentPath, authResolveCallbacksRef.current)
          .catch((err) => {
            console.error('Auth listener resolution error:', err);
          })
          .finally(() => {
            if (!cancelled) {
              setAuthResolving(false);
              setLoading(false);
            }
          });
      }, 0);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [loading, location.pathname]);

  // Handle logout
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      sessionRef.current = null;
      setAuthResolving(false);
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Logout failed. Please try again.');
    }
  };

  if (isPublicLegalRoute) {
    return (
      <div className="App">
        <PrivacyPolicyPage />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <div className="text-xl font-bold mb-4 text-text-primary">Loading...</div>
        <div className="text-text-muted">Please wait while we initialize the application</div>
      </div>
    );
  }

  // Standalone shells for auth routes — bypass currentUser gate entirely.
  // Pattern mirrors /reset-password: render before `if (!currentUser)` so the
  // login form never flashes and session handling stays in the dedicated page.
  if (isResetPasswordRoute) {
    return (
      <div className="App">
        <LiveBetaBanner />
        <main className="min-h-screen">
          <ResetPassword />
        </main>
      </div>
    );
  }

  if (isAuthOnboardingRoute) {
    return (
      <div className="App">
        <LiveBetaBanner />
        <main className="min-h-screen">
          {location.pathname === '/auth/callback' ? (
            <AuthCallback />
          ) : (
            <CompleteProfile
              onClearAuthState={() => {
                sessionRef.current = null;
                setAuthResolving(false);
                setCurrentUser(null);
              }}
            />
          )}
        </main>
      </div>
    );
  }

  // Only block login UI while resolver is actively running — not stale sessionRef
  const pendingSessionResolution = !currentUser && authResolving;

  if (pendingSessionResolution) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <div className="text-xl font-bold mb-4 text-text-primary">Loading...</div>
        <div className="text-text-muted">Please wait while we sign you in</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/login"
          element={(
            <LoginPage
              sessionRef={sessionRef}
              setAuthResolving={setAuthResolving}
              authError={error}
            />
          )}
        />
        <Route
          path="/signup"
          element={(
            <SignupPage
              sessionRef={sessionRef}
              setAuthResolving={setAuthResolving}
              authError={error}
            />
          )}
        />
        <Route
          path="/leagues/middlesex"
          element={(
            <LandingLayout>
              <LandingNav />
              <main className="mx-auto max-w-app-shell px-6 py-6 sm:px-8 lg:px-10">
                <MiddlesexLeaguePage />
              </main>
            </LandingLayout>
          )}
        />
        <Route path="/leagues/*" element={<Navigate to="/leagues/middlesex" replace />} />
        <Route path="/league-preview" element={<Navigate to="/leagues/middlesex" replace />} />
        <Route path="*" element={<PublicCatchAll />} />
      </Routes>
    );
  }

  if (location.pathname === '/login' || location.pathname === '/signup') {
    const params = new URLSearchParams(location.search);
    const dest = location.pathname === '/login'
      ? safeReturnPath(params.get('from'))
      : '/';
    return <Navigate to={dest} replace />;
  }

  // Check if user is site admin
  const isSiteAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin';

  // Unified layout for all authenticated users
  return (
    <div className="App bg-background transition-colors duration-200">
      <LiveBetaBanner onOpenFeedback={() => setShowSiteFeedbackModal(true)} />
      
      {/* Single outer container for consistent max-width across all sections */}
      <div className="max-w-app-shell mx-auto">
        {!isResetPasswordRoute && (
          <AppHeader
            currentUser={currentUser}
            onLogout={handleLogout}
            showNotifications={showNotifications}
            setShowNotifications={setShowNotifications}
            unreadNotificationCount={unreadNotificationCount}
          />
        )}
        
        {/* Breadcrumb Navigation (hide on reset-password page) */}
        {!isResetPasswordRoute && <Breadcrumb />}
        
        {/* Notifications Modal */}
        <Modal
          isOpen={showNotifications && !!currentUser}
          onClose={() => setShowNotifications(false)}
        >
          <ModalContent size="large" padding={false} className="flex min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between border-b border-border p-6">
              <h2 className="text-xl font-semibold text-text-primary">Notifications</h2>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-text-muted hover:text-text-primary"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <NotificationsTab
                currentUser={currentUser}
                onClose={() => setShowNotifications(false)}
              />
            </div>
          </ModalContent>
        </Modal>
        
        <main className="px-6 sm:px-8 lg:px-10 py-4 sm:py-6 min-h-screen overflow-visible">
          <Routes>
          <Route path="/" element={<Home currentUser={currentUser} />} />
          <Route path="/session" element={<Session currentUser={currentUser} selectedClub={selectedClub} setSelectedClub={setSelectedClub} />} />
          <Route path="/session/:sessionId" element={<SessionDashboard currentUser={currentUser} />} />
          <Route path="/club-admin" element={
            <SeasonProvider clubId={selectedClub?.id}>
              <ClubAdmin currentUser={currentUser} selectedClub={selectedClub} />
            </SeasonProvider>
          } />
          <Route path="/create-club" element={<CreateClub currentUser={currentUser} />} />
          {/* Full Leaderboard Page */}
          <Route path="/clubs/:clubId/leaderboard/:gameType" element={<FullLeaderboardPage currentUser={currentUser} />} />
          {/* Public club page - discovery-focused */}
          <Route path="/clubs/:clubId" element={<ClubPage currentUser={currentUser} />} />
          {/* Legacy club route - keep for backward compatibility */}
          <Route path="/club/:clubId" element={<ClubPage currentUser={currentUser} />} />
          {/* New Profile Routes - Mobile-first, route-based */}
          <Route path="/profile" element={<ProfileLayout currentUser={currentUser} />}>
            <Route index element={<ProfileOverview />} />
            <Route path="overview" element={<ProfileOverview />} />
            <Route path="games" element={<ProfileMyGames />} />
            <Route path="sessions" element={<ProfileMySessions />} />
            <Route path="matchups" element={<ProfileHeadToHead />} />
            <Route path="head-to-head" element={<Navigate to="/profile/matchups" replace />} />
            <Route path="clubs" element={<ProfileClubsList />} />
            <Route path="clubs/:clubId" element={<ProfileMyClubs />} />
            <Route path="clubs/:clubId/manage" element={<ProfileClubManagement />} />
            {/* Redirect old profile club routes to Club Dashboard */}
            <Route path="clubs/:clubId/performance" element={<ClubRouteRedirect tab="performance" />} />
            <Route path="clubs/:clubId/h2h" element={<ClubRouteRedirect tab="headtohead" />} />
            <Route path="clubs/:clubId/partnerships" element={<ClubRouteRedirect tab="partnerships" />} />
            <Route path="settings" element={<ProfileSettings onUserUpdate={setCurrentUser} />} />
          </Route>
          {/* Legacy profile route - redirect to new structure */}
          <Route path="/player-dashboard" element={<Navigate to="/profile/clubs" replace />} />
          {/* Keep old profile route for backwards compatibility - redirect to overview */}
          <Route path="/profile-old" element={<PlayerProfilePage currentUser={currentUser} onUserUpdate={setCurrentUser} />} />
          <Route path="/upcoming-sessions" element={<UpcomingSessions currentUser={currentUser} />} />
          {/* Club-contexted routes */}
          <Route path="/club/:clubId/sessions/start" element={<SessionStartPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/sessions/review" element={<SessionReviewPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/sessions/setup/:blockId" element={<ClubSessionPlanning currentUser={currentUser} />} />
          <Route path="/club/:clubId/sessions/setup" element={<ClubSessionPlanning currentUser={currentUser} />} />
          <Route path="/club/:clubId/sessions/:sessionId/signups" element={<SessionSignupsPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/sessions" element={<SessionManagement currentUser={currentUser} />} />
          <Route path="/club/:clubId/session-wrap-up" element={<ClubSessionWrapUpRedirect />} />
          <Route path="/club/:clubId/session/:sessionId" element={<SessionDashboard currentUser={currentUser} />} />
          <Route path="/club/:clubId/session-wrapup/:sessionId" element={<SessionWrapUpDetail currentUser={currentUser} />} />
          <Route path="/club/:clubId/tournaments" element={<TournamentsPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/tournaments/new" element={<CreateTournamentPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/tournaments/:tournamentId" element={<TournamentDetailPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/settings" element={<ClubAdmin currentUser={currentUser} />} />
          <Route path="/club/:clubId/club-settings" element={<ClubSettings currentUser={currentUser} />} />
          <Route path="/club/:clubId/start-session" element={<ClubSessionStartRedirect />} />
          <Route path="/player/:playerId/session/:sessionId" element={<PlayerSessionPerformancePage currentUser={currentUser} />} />
          
          {/* Legacy routes - keep for backwards compatibility, but redirect */}
          <Route path="/session-management" element={<Navigate to="/" replace />} />
          <Route path="/session-monitoring" element={<Navigate to="/" replace />} />
          <Route path="/session-templates" element={<Navigate to="/" replace />} />
          
          <Route path="/manage-clubs" element={<Navigate to="/clubs" replace />} />
          <Route path="/club-management/:clubId" element={<ClubManagement currentUser={currentUser} />} />
          <Route path="/club-analysis" element={<ClubAnalysis currentUser={currentUser} />} />
          <Route path="/club/:clubId/session-simulations" element={<SessionSimulation currentUser={currentUser} />} />
          <Route path="/club/:clubId/payment-tracking" element={<PaymentTrackingPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/league-squad" element={<ClubLeagueSquadPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/paste-names" element={<PasteAliasManagementPage currentUser={currentUser} />} />
          <Route path="/club/:clubId/league-performance" element={<ClubLeaguePlayerPerformancePage currentUser={currentUser} />} />
          <Route path="/clubs" element={<Clubs currentUser={currentUser} />} />
          <Route path="/my-clubs" element={<Navigate to="/clubs" replace />} />
          <Route path="/private-sessions" element={<PrivateSessionsPage currentUser={currentUser} />} />
          <Route path="/account-settings" element={<Navigate to="/profile?tab=account" replace />} />
          <Route path="/release-notes" element={<ReleaseNotes />} />
          <Route path="/matchmaking-info" element={<MatchmakingInfo />} />
          <Route path="/leagues/middlesex" element={<MiddlesexLeaguePage />} />
          <Route path="/leagues/*" element={<Navigate to="/leagues/middlesex" replace />} />
          <Route path="/league-preview" element={<Navigate to="/leagues/middlesex" replace />} />
          <Route path="/league-admin" element={<LeagueAdminPage currentUser={currentUser} />} />
          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
      </div>{/* End of max-w-app-shell outer container */}

      <SiteFeedbackModal
        isOpen={showSiteFeedbackModal}
        onClose={() => setShowSiteFeedbackModal(false)}
        currentUser={currentUser}
      />
    </div>
  );
}

export default function AppWithRouter() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ErrorBoundary>
          <Router>
            <App />
          </Router>
        </ErrorBoundary>
      </ToastProvider>
    </ThemeProvider>
  );
}
